import React, { useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { ChevronLeft, ChevronRight } from "lucide-react";
import dayjs from "dayjs";
import { useSwalOpen } from "../../hooks/use-swal-open";

const MdrawingtableDrawer = ({
  title,
  open,
  rows,
  rowIndex,
  editRow,
  onClose,
  onChangeRow,
  onMovePrev,
  onMoveNext,
  onSave,
  handleShowModal,
  moduleDialogOpen = false,
  areaList = [],
  vendorList = [],
}) => {
  const swalOpen = useSwalOpen();

  React.useEffect(() => {
    if (swalOpen) {
      document.querySelector(".swal2-confirm")?.focus();
    }
  }, [swalOpen]);

  if (!editRow) return null;

  const handleChange = (key, newValue) => {
    if (key === "DPsubPD") {
      onChangeRow({ ...editRow, DPsubPD: newValue, DPMType: "" });
    } else {
      onChangeRow({ ...editRow, [key]: newValue });
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => !v && onClose()}
      modal={!moduleDialogOpen && !swalOpen}
    >
      <SheetContent
        side="right"
        aria-describedby={undefined}
        className="flex w-[440px] max-w-[440px] flex-col gap-0 p-0 sm:max-w-[440px] [&_label]:text-xs [&_input]:!text-xs"
        onInteractOutside={(e) => {
          if (moduleDialogOpen || swalOpen) e.preventDefault();
        }}
        onFocusOutside={(e) => {
          if (moduleDialogOpen || swalOpen) e.preventDefault();
        }}
      >
        {/* ===== Header (고정) ===== */}
        <div className="flex shrink-0 items-center justify-center gap-2 border-b px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onMovePrev}
            disabled={rowIndex === 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <SheetTitle className="text-base font-semibold">
            {title} · {rowIndex + 1} / {rows.length}
          </SheetTitle>

          <Button
            variant="ghost"
            size="icon"
            onClick={onMoveNext}
            disabled={rowIndex === rows.length - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* ===== Scroll Area ===== */}
        <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-2">
          {/* 레코드번호 */}
          <div className="space-y-1.5">
            <Label>레코드 번호</Label>
            <Input type="text" value={editRow["record_no"] ?? ""} disabled />
          </div>
          {/* 공종* */}
          <ComboboxField
            label="공종"
            value={editRow["DrawingDiscPD"]}
            options={["설비"]}
            onChange={(v) => handleChange("DrawingDiscPD", v)}
          />
          {/* 상세공종* */}
          <ComboboxField
            label="상세공종"
            value={editRow["DPsubPD"]}
            options={["배관", "HVAC"]}
            onChange={(v) => handleChange("DPsubPD", v)}
          />
          {/* 구분* */}
          <ComboboxField
            label="구분"
            value={editRow["DPMType"]}
            options={
              editRow["DPsubPD"] === "HVAC"
                ? ["GAL'V", "GAL'V 외"]
                : ["-", "모듈", "비모듈"]
            }
            onChange={(v) => handleChange("DPMType", v)}
          />
          {/* Area* */}
          <ComboboxField
            label="Area"
            value={editRow["DPAreaBPK"]}
            options={areaList}
            onChange={(v) => handleChange("DPAreaBPK", v)}
          />
          {/* 층* */}
          <div className="space-y-1.5">
            <Label>층</Label>
            <Input
              type="text"
              value={editRow["DPFloor"] ?? ""}
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  DPFloor: e.target.value,
                })
              }
            />
          </div>
          {/* 도면(문서) No* */}
          <div className="space-y-1.5">
            <Label>도면(문서) No</Label>
            <Input
              type="text"
              value={editRow["DrawingNo"] ?? ""}
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  DrawingNo: e.target.value,
                })
              }
            />
          </div>
          {/* 도면구분* */}
          <ComboboxField
            label="도면구분"
            value={editRow["DrawingClassification"]}
            options={["승인도", "제작도"]}
            onChange={(v) => handleChange("DrawingClassification", v)}
          />

          {/* 성상* */}
          <div className="space-y-1.5">
            <Label>성상</Label>
            <Input
              type="text"
              value={editRow["DrawingSystem"] ?? ""}
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  DrawingSystem: e.target.value,
                })
              }
            />
          </div>
          {/* 재질* */}
          <div className="space-y-1.5">
            <Label>재질</Label>
            <Input
              type="text"
              value={editRow["DrawingMaterial"] ?? ""}
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  DrawingMaterial: e.target.value,
                })
              }
            />
          </div>
          {/* 사이즈* */}
          <div className="space-y-1.5">
            <Label>사이즈</Label>
            <Input
              type="text"
              value={editRow["DrawingSize"] ?? ""}
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  DrawingSize: e.target.value,
                })
              }
            />
          </div>
          {/* 리비전* */}
          <div className="space-y-1.5">
            <Label>리비전</Label>
            <Input
              type="text"
              value={editRow["DPRevNo"] ?? ""}
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  DPRevNo: e.target.value,
                })
              }
            />
          </div>
          {/* Module No */}
          <div className="space-y-1.5">
            <Label>모듈넘버</Label>
            <Input
              type="button"
              className="text-left"
              onClick={() => handleShowModal(editRow.id)}
              value={editRow["MDModuleNoDPK"] ?? ""}
              readOnly
            />
          </div>
          {/* Module 입고일 */}
          <div className="space-y-1.5">
            <Label>모듈 입고일</Label>
            <Input
              type="date"
              value={
                editRow["MDSitePlanDO"]
                  ? dayjs(editRow["MDSitePlanDO"]).format("YYYY-MM-DD")
                  : ""
              }
              disabled
            />
          </div>

          {/* 기준 날짜 */}
          <div className="space-y-1.5">
            <Label>기준 날짜</Label>
            <Input
              type="date"
              value={
                editRow["WStart"] ? dayjs(editRow["WStart"]).format("YYYY-MM-DD") : ""
              }
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  WStart: new Date(e.target.value),
                })
              }
            />
          </div>
          {/* 승인도 도면 No* */}
          <div className="space-y-1.5">
            <Label>승인도 도면 No</Label>
            <Input
              type="text"
              value={editRow["ApprovedDrawingNotxt"] ?? ""}
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  ApprovedDrawingNotxt: e.target.value,
                })
              }
            />
          </div>
          {/* 협력업체* */}
          <ComboboxField
            label="협력업체"
            value={editRow["VendorsDPK"]}
            options={vendorList}
            onChange={(v) => handleChange("VendorsDPK", v)}
            searchable
          />
          {/* 선출도 계획일 */}
          <div className="space-y-1.5">
            <Label>선출도 계획일</Label>
            <Input
              type="date"
              value={
                editRow["ReceiptScheduledDate"]
                  ? dayjs(editRow["ReceiptScheduledDate"]).format("YYYY-MM-DD")
                  : ""
              }
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  ReceiptScheduledDate: new Date(e.target.value),
                })
              }
            />
          </div>
          {/* 도면(BIM) 송부 계획일 */}
          <div className="space-y-1.5">
            <Label>도면(BIM) 송부 계획일</Label>
            <Input
              type="date"
              value={
                editRow["BIMPlanDate"]
                  ? dayjs(editRow["BIMPlanDate"]).format("YYYY-MM-DD")
                  : ""
              }
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  BIMPlanDate: new Date(e.target.value),
                })
              }
            />
          </div>
          {/* 제작도 출도 계획일 */}
          <div className="space-y-1.5">
            <Label>제작도 출도 계획일</Label>
            <Input
              type="date"
              value={
                editRow["DrawingReleasePlanDate"]
                  ? dayjs(editRow["DrawingReleasePlanDate"]).format("YYYY-MM-DD")
                  : ""
              }
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  DrawingReleasePlanDate: new Date(e.target.value),
                })
              }
            />
          </div>
        </div>

        {/* ===== Footer (고정) ===== */}
        <div className="flex shrink-0 gap-2 border-t px-4 py-3">
          <Button className="flex-1" onClick={onSave}>
            저장
          </Button>
          <Button variant="outline" className="flex-1" onClick={onClose}>
            닫기
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MdrawingtableDrawer;

const ComboboxField = ({ label, value, options, onChange, searchable = false }) => {
  const [openPopover, setOpenPopover] = useState(false);
  const [query, setQuery] = useState("");

  const filteredOptions = searchable
    ? options.filter((opt) => opt.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Popover open={openPopover} onOpenChange={setOpenPopover}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-9 w-full items-center justify-start rounded-md border border-input bg-[#f9fcff] px-3 py-1 text-left text-xs shadow-[0_1px_2px_0_rgb(0,0,0,0.05)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {value ? (
              <span className="truncate">{value}</span>
            ) : (
              <span className="text-muted-foreground">선택해주세요</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2" align="start">
          {searchable && (
            <Input
              type="text"
              placeholder="검색"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="mb-2"
              autoFocus
            />
          )}
          <div className="max-h-60 space-y-1 overflow-y-auto">
            {filteredOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                onClick={() => {
                  onChange(opt);
                  setOpenPopover(false);
                  setQuery("");
                }}
              >
                {opt}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
